import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, CheckCircle2, Loader2, FileText, AlertCircle } from "lucide-react";
import { api } from "@/lib/api";

export default function Preview() {
  const nav = useNavigate();
  const [preview, setPreview] = useState(null);
  const [accepting, setAccepting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    const raw = sessionStorage.getItem("hireflow_preview");
    if (!raw) {
      nav("/");
      return;
    }
    setPreview(JSON.parse(raw));
  }, [nav]);

  if (!preview) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="animate-spin text-blue-700" size={32} />
      </div>
    );
  }

  const pdfUrl = `data:application/pdf;base64,${preview.pdf_base64}`;

  async function handleAccept() {
    setError("");
    setAccepting(true);
    try {
      const res = await api.post(`/submissions/${preview.id}/accept`);
      sessionStorage.setItem("hireflow_sent", JSON.stringify(res.data));
      sessionStorage.removeItem("hireflow_preview");
      sessionStorage.removeItem("hireflow_form_data");
      localStorage.removeItem("hireflow_draft");
      nav("/success");
    } catch (err) {
      const msg = err?.response?.data?.detail || "Σφάλμα κατά την αποστολή. Προσπαθήστε ξανά.";
      setError(msg);
    } finally {
      setAccepting(false);
    }
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-gradient-to-br from-blue-700 to-blue-900 text-white">
        <div className="max-w-5xl mx-auto px-4 md:px-8 py-10 flex items-center justify-between">
          <div>
            <div className="flex items-center gap-2 text-blue-200 text-xs font-bold tracking-[0.2em] uppercase mb-3">
              <span className="inline-block w-8 h-px bg-blue-300"></span>
              Βήμα 2 από 2
            </div>
            <h1 className="text-3xl md:text-4xl font-extrabold tracking-tight">Προεπισκόπηση & Έγκριση</h1>
            <p className="text-blue-100 mt-2 text-sm md:text-base font-medium">
              Ελέγξτε προσεκτικά τα στοιχεία. Με το πάτημα της <b>Οριστικής Υποβολής</b> το PDF αποστέλλεται στο λογιστήριο.
            </p>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 md:px-8 py-10">
        <div className="bg-white border border-slate-200 rounded-2xl shadow-[0_8px_30px_-12px_rgba(0,0,0,0.1)] overflow-hidden hf-fade-up">
          <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between bg-slate-50/50">
            <div className="flex items-center gap-3">
              <FileText className="text-blue-700" size={20} />
              <span className="text-sm font-bold text-slate-700">
                Φόρμα Πρόσληψης (PDF){preview.resume_files_count > 0 && (
                  <span className="text-slate-500 font-medium"> + {preview.resume_files_count} αρχείο(α): {(preview.resume_files_names || []).join(", ")}</span>
                )}
              </span>
            </div>
            <a
              href={pdfUrl}
              download={`hiring_${preview.id.slice(0, 8)}.pdf`}
              className="text-xs font-bold text-blue-700 hover:text-blue-900 underline"
              data-testid="download-pdf-link"
            >
              Λήψη PDF
            </a>
          </div>
          <iframe
            title="PDF Preview"
            src={pdfUrl}
            className="w-full"
            style={{ height: "78vh", minHeight: 600, border: 0 }}
            data-testid="pdf-iframe"
          />
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-4 mt-6 text-sm text-red-700 font-semibold flex items-start gap-3" data-testid="accept-error">
            <AlertCircle size={20} className="shrink-0 mt-0.5" />
            <span>{error}</span>
          </div>
        )}

        <div className="flex flex-col md:flex-row gap-3 justify-between mt-8">
          <button
            type="button"
            onClick={() => nav(-1)}
            className="h-14 px-6 bg-white border border-slate-200 hover:border-slate-300 hover:bg-slate-50 text-slate-700 font-bold rounded-xl transition-all flex items-center justify-center gap-2"
            data-testid="edit-back-btn"
          >
            <ArrowLeft size={18} /> Επεξεργασία
          </button>
          <button
            type="button"
            onClick={handleAccept}
            disabled={accepting}
            className="h-14 px-10 bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-lg rounded-xl shadow-[0_8px_20px_-8px_rgba(5,150,105,0.5)] active:scale-[0.98] transition-all flex items-center justify-center gap-2 disabled:opacity-60"
            data-testid="accept-submit-btn"
          >
            {accepting ? <><Loader2 className="animate-spin" size={20} /> Αποστολή…</> : <><CheckCircle2 size={20} /> Οριστική Υποβολή & Αποστολή</>}
          </button>
        </div>
      </main>
    </div>
  );
}
